import { Component, inject, signal } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HeaderComponent } from './shared/components/header/header.component';
import { ToasterComponent } from './shared/components/toaster/toaster.component';
import { LogoutService } from './services/logout.service';
import { CommonModule } from '@angular/common';
import { KeycloakService } from 'keycloak-angular';

@Component({
  imports: [CommonModule, RouterModule, HeaderComponent, ToasterComponent],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  protected title = 'Healthcare';

  isLoggingOutAfterPasswordChange = false;

  /** Show shared header chrome across the catalogue, including the public landing route. */
  protected showMainChrome = signal(true);

  private logoutService = inject(LogoutService);
  private keycloak = inject(KeycloakService);
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);

  private justDetectedPasswordChange = false;

  constructor() {
    this.showMainChrome.set(true);

    // Tell Angular's anchor scroller to leave room for the fixed header.
    // We measure the live header element so the offset is always accurate
    // regardless of breakpoint — falls back to 80px before first paint.
    this.viewportScroller.setOffset(() => {
      const header = document.querySelector<HTMLElement>('.header');
      return [0, header?.offsetHeight ?? 0];
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.showMainChrome.set(true);
      });

    this.checkPasswordChangeRedirect();

    if (!this.justDetectedPasswordChange) {
      this.checkPendingPasswordChangeLogout();
    }
  }

  private checkPendingPasswordChangeLogout(): void {
    const logoutPendingData = localStorage.getItem('password-changed-logout-pending');
    let logoutPending = null;

    try {
      logoutPending = logoutPendingData ? JSON.parse(logoutPendingData) : null;
    } catch (e) {
      console.error('[App] Error parsing logout pending flag:', e);
    }

    const isPending =
      logoutPending?.pending && Date.now() - logoutPending.timestamp < 2 * 60 * 1000;

    if (isPending) {
      console.log('[App] Detected pending password change logout - forcing logout now');

      localStorage.removeItem('password-changed-logout-pending');

      this.isLoggingOutAfterPasswordChange = true;

      console.log('[App] Forcing logout after auto-login...');
      this.forceImmediateLogout();
    }
  }

  private checkPasswordChangeRedirect(): void {
    const fullUrl = window.location.href;
    const hashPart = window.location.hash || '';

    const hasPasswordAction = fullUrl.includes('kc_action=UPDATE_PASSWORD');
    const hasSuccessStatus = fullUrl.includes('kc_action_status=success');
    const keycloakReportsSuccess = hasPasswordAction && hasSuccessStatus;

    const passwordChangeData = localStorage.getItem('keycloak_password_change_flow');
    let localStorageFlag = null;
    try {
      localStorageFlag = passwordChangeData ? JSON.parse(passwordChangeData) : null;
    } catch (e) {
      console.error('[App] Error parsing localStorage flag:', e);
    }

    const hasValidFlag =
      localStorageFlag?.initiated &&
      Date.now() - localStorageFlag.timestamp < 10 * 60 * 1000;

    const hasKeycloakResponse = hashPart.includes('state=') && hashPart.includes('code=');

    const localStorageReportsSuccess = hasValidFlag && hasKeycloakResponse;

    console.log('[App] Checking password change redirect:', {
      fullUrl: fullUrl.substring(0, 250),
      keycloakReportsSuccess,
      hasPasswordAction,
      hasSuccessStatus,
      localStorageReportsSuccess,
      hasValidFlag,
      hasKeycloakResponse,
      localStorageFlag,
    });

    if (keycloakReportsSuccess || localStorageReportsSuccess) {
      console.log('[App] Password change SUCCESS detected, initiating logout...', {
        method: keycloakReportsSuccess ? 'keycloak_params' : 'localStorage_flag',
      });

      this.justDetectedPasswordChange = true;

      localStorage.removeItem('keycloak_password_change_flow');

      this.isLoggingOutAfterPasswordChange = true;

      window.history.replaceState({}, '', window.location.pathname);

      console.log('[App] Executing logout after password change...');

      localStorage.setItem(
        'password-changed-logout-pending',
        JSON.stringify({
          pending: true,
          timestamp: Date.now(),
        })
      );
      console.log('[App] Set localStorage flag: password-changed-logout-pending');

      this.forceImmediateLogout();
    }
  }

  private forceImmediateLogout(): void {
    try {
      const kcInstance = this.keycloak.getKeycloakInstance();

      kcInstance.clearToken();

      const tokenKeysToRemove = ['iudx-ui-cat-auth-token', 'ai-sandbox-cat-auth-token'];
      tokenKeysToRemove.forEach((key) => {
        try {
          localStorage.removeItem(key);
        } catch {
          // Ignore
        }
      });

      const logoutUrl = kcInstance.createLogoutUrl({
        redirectUri: window.location.origin,
      });

      console.log('[App] Redirecting to Keycloak logout:', logoutUrl);

      window.location.href = logoutUrl;
    } catch (error) {
      console.error('[App] Error during force logout:', error);
      localStorage.removeItem('password-changed-logout-pending');
      window.location.href = '/';
    }
  }
}
